import { httpService, formatDate, getTimeGranularityOverview } from '@flexo/general';
import type { RankingStrategy, Scope, Domain } from '@flexo/resolvers/src/lib/ranking/strategy';

export async function fetchRankingPage({
  strategy, baseUrl, scope, domain, entityId, selectedDate, selectedTimeSet, locale, pageSize, offset
}: {
  strategy: RankingStrategy; baseUrl: string; scope: Scope; domain: Domain; entityId: string;
  selectedDate: [Date, Date]; selectedTimeSet: any; locale: string; pageSize: number; offset: number;
}) {
  const url = strategy.buildEndpoint(baseUrl, scope, domain, entityId);
  const { rankingKpis, kpiLists } = strategy.getRankingConfig(domain);
  // Token auto-injected by httpService interceptor
  const res = await httpService.post({
    url,
    data: {
      dateFrom: formatDate(selectedDate[0]),
      dateTo: formatDate(selectedDate[1]),
      timeGranularity: getTimeGranularityOverview(selectedTimeSet),
      locale, kpiLists, rankingKpis,
      limitTopN: pageSize,
      offset,
    }
  }).catch(() => null);
  if (!res || res.status !== 200) { return null; }
  const data = res.data;
  // API returns the ranking array directly, not wrapped under a key.
  // Normalize by domain so downstream merge code can rely on stable keys.
  return domain === 'energy' ? { energyRanking: data } : { economicsRanking: data };
}

export function mergeRankingPages(pages: any[]) {
  if (!pages.length) return null;
  const base = JSON.parse(JSON.stringify(pages[0]));
  const keys = ['energyRanking','economicsRanking','costsRanking'];
  for (let i = 1; i < pages.length; i++) {
    const page = pages[i];
    for (const key of keys) {
      if (!base[key] || !page?.[key]) continue;
      base[key].forEach((grp: any, gi: number) => {
        grp.rankings?.forEach((rk: any, ri: number) => {
          const src = page[key]?.[gi]?.rankings?.[ri];
          if (src?.site?.length) rk.site = [...(rk.site || []), ...src.site];
        });
      });
    }
  }
  return base;
}

export async function fetchRankingPagedSequential(params: {
  strategy: RankingStrategy; baseUrl: string; scope: Scope; domain: Domain; entityId: string;
  selectedDate: [Date, Date]; selectedTimeSet: any; locale: string; totalSites: number;
}) {
  const { strategy, baseUrl, scope, domain, entityId, selectedDate, selectedTimeSet, locale, totalSites } = params;
  const pages: any[] = [];
  const step = strategy.pageSize;

  for (let offset = 0; offset < totalSites; offset += step) {
    const page = await fetchRankingPage({
      strategy, baseUrl, scope, domain, entityId, selectedDate, selectedTimeSet, locale, pageSize: step, offset
    });
    if (!page) break;
    pages.push(page);
    const energySample = page.energyRanking && page.energyRanking[0]?.rankings?.[0]?.site;
    const economicsSample = page.economicsRanking && page.economicsRanking[0]?.rankings?.[0]?.site;
    const costsSample = page.costsRanking && page.costsRanking[0]?.rankings?.[0]?.site;
    const sample = energySample || economicsSample || costsSample || [];
    if ((sample?.length || 0) < step) break;
  }
  return mergeRankingPages(pages);
}


